import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
	ChangeDetectorRef,
	OnDestroy,
} from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import {
	ContactDTO,
	ContactsTable,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	RestService,
	TruckSearchTable,
} from 'cargo-chief-commons';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { CarrierDataFeedbackDialog } from '../../project/capacity-search-panel/carrier-data-feedback/carrier-data-feedback.component';
import { AddContactFormDialog } from '../add-contact/add-contact.component';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { environment } from '@environment/environment';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { COMPARE } from '@domain/constants/functions';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { CarrierFeedbackService } from '@service/carrierFeedback/carrier-feedback.service';
import moment from 'moment';
import { SearchContactsDialogComponent } from '../search-contacts-modal/search-contacts-modal.component';
import { ConfirmationDialogComponent } from '../../common/confirmation-dialog/confirmation-dialog.component';
import { BaseComponent } from '@shared/base/base.component';
import { SharedService } from '@service/shared/shared.service';

import { takeUntil } from 'rxjs/operators';
import { take } from 'rxjs';

@Component({
	selector: 'app-carrier-contacts',
	templateUrl: './carrier-contacts.component.html',
	styleUrls: ['./carrier-contacts.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarrierContactsComponent extends BaseComponent implements OnInit, OnDestroy {
	@Input() contactList;
	@Input() carrierId;
	@Input() oldCarrierId;
	@Input() carrier;
	@Input() isCarrierProfile: boolean = false;
	@Input() editPrimaryContact: boolean = false;
	@Input() incorrectContactInfoEnabled: boolean = false;
	@Input() lane: TruckSearchTable;
	@Input() isEditContact: boolean = false;
	@Input() isCarrierUpdate: boolean = false;
	@Input() isCarrierLoggedIn: boolean = false;
	@Input() searchTruckForm = null;
	@Input() showContactSearchIcon = false;
	@Input() mapContactToProjectColumnEnabled: boolean = false;
	@Input() projectCarrierMap!: any;
	@Input() rowSelectionEnabled: boolean = false;
	@Input() showAddContactIcon: boolean = true;

	@Output() updateContactsList: EventEmitter<any> = new EventEmitter();
	@Output() onProjectCarrierMapContact: EventEmitter<any> = new EventEmitter();

	hasPrioritizedContact: boolean = false;
	isContactMappedToProject: boolean = false;

	PAGE_SIZE = 10;
	totalNumberOfContacts = 0;

	contactsDataTable = new MatTableDataSource<ContactsTable>([]);
	displayedColumns = [];
	carrierProfileColumns = [];
	selectedContactRow!: ContactsTable;

	@ViewChild(MatSort) sort: MatSort;
	@ViewChild(MatPaginator) paginator: MatPaginator;

	environment = environment;
	IS_NOT_NULL_OR_UNDEFINED = IS_NOT_NULL_OR_UNDEFINED;

	constructor(
		private urlService: UrlService,
		private restService: RestService,
		public utilService: UtilService,
		public dialog: MatDialog,
		private segmentWrapper: SegmentWrapperService,
		public cargochief: Cargochief,
		private carrierFeedbackService: CarrierFeedbackService,
		private cdRef: ChangeDetectorRef,
		private sharedService: SharedService
	) {
		super();
		this.sharedService
			.getAddNewProjectCarrierMapContactEvent()
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe((data: any) => {
				if (IS_NOT_NULL_OR_UNDEFINED(this.carrierId) && data?.carrierId === this.carrierId) {
					this.openContactDialog(null);
				}
			});
	}

	ngOnInit() {
		if (environment.SHOW_CONTACT_AUTHORIZED) {
			this.displayedColumns = [
				'firstName',
				'lastName',
				'email',
				'emailValidation',
				'phoneNumber',
				'primaryContact',
				'isMailReciever',
				'createdAt',
				'actions',
			];
			this.carrierProfileColumns = [
				'firstName',
				'lastName',
				'email',
				'emailValidation',
				'phoneNumber',
				'mobileNumber',
			];
		} else {
			this.displayedColumns = [
				'firstName',
				'lastName',
				'email',
				'phoneNumber',
				'primaryContact',
				'isMailReciever',
				'createdAt',
				'actions',
			];
			this.carrierProfileColumns = [
				'firstName',
				'lastName',
				'email',
				'phoneNumber',
				'mobileNumber',
			];
		}

		if (this.isCarrierProfile) {
			this.displayedColumns = [...this.carrierProfileColumns];
			if (this.editPrimaryContact) {
				this.displayedColumns.push('primaryContact');
			}
		}
		if (this.incorrectContactInfoEnabled) {
			this.displayedColumns.push('contactFeedback');
		}
		if (this.isEditContact) {
			this.displayedColumns.push('editColumn');
		}
		this.displayedColumns.push('reportColumn');
		if (this.mapContactToProjectColumnEnabled) {
			this.displayedColumns.push('mapContactToProjectColumn');
		}
	}

	ngOnChanges() {
		this.buildDatatableView();
	}

	buildDatatableView() {
		this.hasPrioritizedContact = false;
		this.isContactMappedToProject = false;
		if (!this.utilService.isNullOrUndefined(this.contactList) && this.contactList.length > 0) {
			const projectContactIds = this.projectCarrierMap?.contactDTOs
				? this.projectCarrierMap.contactDTOs.map((contact) => contact['id'])
				: [];

			this.contactList.forEach((contact) => {
				if (
					this.projectCarrierMap?.contactDTO?.contactId &&
					contact.contactId === this.projectCarrierMap.contactDTO.contactId
				) {
					contact.contactMappedToProject = true;
					this.isContactMappedToProject = true;
				} else if (projectContactIds.includes(contact.contactId)) {
					contact.contactMappedToProject = true;
					this.isContactMappedToProject = true;
				} else {
					contact.contactMappedToProject = false;
					if (contact.prioritizedContact) {
						this.hasPrioritizedContact = true;
					}
				}
			});

			this.contactList = this.contactList.filter((contact: ContactsTable) => {
				if (
					IS_NOT_EMPTY(contact.firstName) ||
					IS_NOT_EMPTY(contact.lastName) ||
					IS_NOT_EMPTY(contact.emailAddress) ||
					IS_NOT_EMPTY(contact.phone) ||
					IS_NOT_EMPTY(contact.mobile)
				) {
					return contact;
				}
			});
			this.sortContactList();
			this.contactsDataTable = new MatTableDataSource(this.contactList);
		} else {
			this.contactsDataTable = new MatTableDataSource([]);
		}
		this.totalNumberOfContacts = this.contactsDataTable.data.length;
		setTimeout(() => {
			this.initializeSortAndPage();
		});
	}

	initializeSortAndPage() {
		this.contactsDataTable.paginator = this.paginator;
		this.contactsDataTable.sort = this.sort;
		this.cdRef.detectChanges();
	}

	sortContactList() {
		let prioritizedContactsList = [],
			contactsList = [];
		this.contactList.forEach((contact) => {
			if (
				!this.isContactMappedToProject &&
				((!this.hasPrioritizedContact && contact.primaryContact) || contact.prioritizedContact)
			) {
				prioritizedContactsList.push(contact);
			} else if (this.isContactMappedToProject && contact.contactMappedToProject) {
				prioritizedContactsList.push(contact);
			} else {
				contactsList.push(contact);
			}
		});
		contactsList = contactsList.sort((a, b) => {
			if (IS_NOT_NULL_OR_UNDEFINED(a.firstName) && IS_NOT_NULL_OR_UNDEFINED(b.firstName)) {
				if (a.firstName.toLowerCase() === b.firstName.toLowerCase()) {
					if (IS_NOT_NULL_OR_UNDEFINED(a.lastName) && IS_NOT_NULL_OR_UNDEFINED(b.lastName)) {
						return COMPARE(a.lastName.toLowerCase(), b.lastName.toLowerCase(), true);
					}
					return -1;
				}
				return COMPARE(a.firstName.toLowerCase(), b.firstName.toLowerCase(), true);
			}
			return -1;
		});
		this.contactList = prioritizedContactsList.concat(contactsList);
	}

	async primaryContactToggled(event, toggledContact) {
		let isPrimaryContact = event.checked;
		let carrierContactId = toggledContact.id;
		let allowPrimaryContact = true;
		// If the contact is not authorized and we are trying to make it primary.
		// Then open a dialog to validate the change.
		if (!toggledContact.vettingAuthorized && isPrimaryContact) {
			allowPrimaryContact = await this.openNotValidatedContactDialog();
		}

		if (!allowPrimaryContact) {
			event.source.checked = false;
			return;
		}

		let updatedContact: ContactsTable = Object.assign(
				{},
				this.contactList.find((contact) => contact.id == carrierContactId)
			),
			contact = Object.assign({}, updatedContact);
		contact.primaryContact = !isPrimaryContact;
		updatedContact.primaryContact = isPrimaryContact;
		this.fireContactEvent(true, updatedContact, contact);
		const url = this.urlService.updatePrimaryContact(
			isPrimaryContact,
			carrierContactId,
			this.carrierId
		);
		this.restService.POST(url, {}).subscribe((result) => {});
	}

	isMailRecieverToggled(isPrimaryContact, contactId) {
		const url = this.urlService.updateIsMailReciever(isPrimaryContact, contactId, this.carrierId);
		this.restService.POST(url, {}).subscribe((result) => {});
	}

	contactFeedback(contact: ContactsTable) {
		const dialogRef = this.dialog.open(CarrierDataFeedbackDialog, {
			width: '900px',
			data: {
				lane: this.lane,
				incorrectDataType: 'contact',
				contact: contact,
			},
		});
	}

	openContactDialog(contact: ContactsTable) {
		const isEditContact = !this.utilService.isNullOrUndefined(contact);
		const dialogRef = this.dialog.open(AddContactFormDialog, {
			width: '500px',
			data: {
				carrierid: this.carrierId,
				isEdit: isEditContact,
				contact: contact,
			},
		});
		dialogRef.afterClosed().subscribe((result) => {
			if (!this.utilService.isNullOrUndefined(result)) {
				if (isEditContact) {
					const indexUpdated = this.contactList.findIndex((contact) => {
						return contact.contactId == result.contactId;
					});
					if (this.isCarrierUpdate) {
						this.fireContactEvent(isEditContact, result, this.contactList[indexUpdated]);
					}
					this.contactList[indexUpdated] = Object.assign({}, result);
				} else {
					this.fireContactEvent(isEditContact, result);
					this.updateContactsListView(result);
				}
				this.updateContactsList.emit(this.contactList);
			}
		});
	}

	fireContactEvent(
		isEditContact: boolean,
		updatedContact: ContactsTable,
		contact: ContactsTable = null
	) {
		if (this.isCarrierLoggedIn) {
			let changedProperties = {},
				eventName = SegmentEventsConstants.carrier_added_contact;
			if (isEditContact) {
				eventName = SegmentEventsConstants.carrier_updated_contact;
				Object.keys(updatedContact)
					.filter((k) => updatedContact[k] !== contact[k])
					.filter((key) => key != 'updatedAt')
					.filter((key) => {
						changedProperties[key] = updatedContact[key];
					});
				changedProperties = JSON.stringify(changedProperties)
					.replace(/\"/g, '')
					.replace(/\{/g, '')
					.replace(/\}/g, '');
			} else {
				changedProperties = `New contact added`;
			}
			const payload: any = {
				contact: updatedContact,
				carrier: this.cargochief.CARRIER,
				url: `${environment.clientUrl}admin/carrier_profile/${this.carrierId}`,
				updated: changedProperties,
			};
			this.segmentWrapper.fireSegmentEvent(eventName, payload);
		}
	}

	async reportDataAction(dataType: string) {
		await this.carrierFeedbackService.reportDataAction(dataType, this.searchTruckForm, this.lane);
	}

	emailValidatedTooltip(contact) {
		return IS_NULL_OR_UNDEFINED(contact.lastVettingTime)
			? ''
			: `Contact validated on ${moment(new Date(contact.lastVettingTime)).format('MM/DD/YYYY')}`;
	}

	showContactSearchDialog() {
		const dialogRef = this.dialog.open(SearchContactsDialogComponent, {
			width: '600px',
			data: {
				carrierId: this.carrierId,
				contactList: this.contactList,
			},
		});

		dialogRef.afterClosed().subscribe((contact) => {
			if (contact) {
				this.mapContactToProject(
					contact.emailAddress,
					this.projectCarrierMap.id,
					contact.contactId
				);
			}
		});
	}

	async mapContactToProjectColumn(
		contact: ContactsTable,
		removeContact: boolean = false,
		skipConfirmation: boolean = false
	) {
		this.mapContactToProject(
			contact.emailAddress,
			contact.id,
			contact.contactId,
			removeContact,
			skipConfirmation
		);
	}

	async mapContactToProject(
		contactEmailAddress: string,
		projectCarrierMapId: string,
		contactId: string,
		removeContact: boolean = false,
		skipConfirmation: boolean = false
	) {
		let confirmationResponse = true;
		if (!removeContact && !skipConfirmation) {
			confirmationResponse = await this.dialog
				.open(ConfirmationDialogComponent, {
					width: '500px',
					data: {
						title: '',
						body: `You are setting ${contactEmailAddress} as the contact for ${this.carrier?.businessName || ''} for all loads matching this project. Loads offers will go to this contact.`,
						cancelButtonText: 'Cancel',
						ConfirmButtonTest: 'Confirm',
					},
					disableClose: true,
				})
				.afterClosed()
				.toPromise()
				.then((response) => {
					return response === true;
				});
		}
		if (!confirmationResponse) {
			this.selectedContactRow = null;
			this.cdRef.detectChanges();
			return;
		}

		const updateRequest = {
			accountId: this.cargochief.ACCOUNT_ID,
			projectCarrierMapId,
			contactId,
		};

		const restRequest = removeContact
			? this.restService.DELETE(
					this.urlService.updateProjectCarrierMapContact(this.projectCarrierMap.id),
					updateRequest
				)
			: this.restService.POST(
					this.urlService.updateProjectCarrierMapContact(this.projectCarrierMap.id),
					updateRequest
				);

		restRequest.subscribe(
			(response) => {
				let updatedPcm = Object.assign({}, this.projectCarrierMap);
				updatedPcm = response;
				this.onProjectCarrierMapContact.emit(updatedPcm);
				if (!skipConfirmation) {
					this.utilService.showSnackbar(
						'Carrier contact for project updated',
						SNACKBAR.SUCCESS,
						SNACKBAR.ACTION
					);
				}

				const indexUpdated = this.contactList.findIndex((c) => c.contactId == contactId);
				const contact = this.contactList.find((c) => c.contactId == contactId);
				this.contactList.forEach(
					(c) => (c.contactMappedToProject = !removeContact && c.contactId == contactId)
				);
				this.isContactMappedToProject = removeContact ? false : true;
				this.contactList[indexUpdated] = Object.assign(
					{},
					this.contactList.find((c) => c.contactId == contactId)
				);
				this.updateContactsList.emit(this.contactList);
				this.selectedContactRow = null;

				this.cdRef.detectChanges();
			},
			(error) => {
				this.utilService.showSnackbar(
					'Unable to set project contact.',
					SNACKBAR.ERROR,
					SNACKBAR.ACTION
				);
			}
		);
	}

	async removeProjectCarrierContactMap(contact: ContactsTable) {
		const confirmationResponse = await this.dialog
			.open(ConfirmationDialogComponent, {
				width: '500px',
				data: {
					title: '',
					body: `This is the primary contact for all loads matching this project. Click the Revert button to restore the default primary contact for this carrier.`,
					cancelButtonText: 'Cancel',
					ConfirmButtonTest: 'Revert',
				},
				disableClose: true,
			})
			.afterClosed()
			.toPromise()
			.then((response) => {
				return response === true;
			});
		if (confirmationResponse) {
			this.mapContactToProjectColumn(contact, true);
		}
	}

	async updateContactsListView(newContact: ContactsTable) {
		const contactsUrl = this.urlService.contactsUrl(this.carrierId);
		const contactsListResponse = await this.restService.GET(contactsUrl).toPromise();
		if (contactsListResponse?.data) {
			this.contactList = contactsListResponse.data;
			this.updateContactsList.emit(this.contactList);
			this.cdRef.detectChanges();
			const contact = this.contactList.find((c) => c.id === newContact.contactId);
			if (contact && this.projectCarrierMap) {
				this.mapNewContactAddedToProject(contact);
			}
		}
	}

	async mapNewContactAddedToProject(contact: ContactsTable) {
		this.mapContactToProjectColumn(contact);
	}

	onContactRowSelection(contact: ContactsTable): void {
		this.selectedContactRow = this.selectedContactRow?.id === contact.id ? null : contact;
	}

	async openNotValidatedContactDialog() {
		return await this.dialog
			.open(ConfirmationDialogComponent, {
				width: '500px',
				data: {
					title: 'Contact is not validated',
					body: `You are setting setting a contact that is not validated as the primary contact.
						This will trigger an automated email to the carrier to confirm the contact.
						Please call the carrier after and ask them to look for the email. `,
					cancelButtonText: 'Cancel',
					ConfirmButtonTest: 'Confirm',
				},
				disableClose: true,
			})
			.afterClosed()
			.toPromise()
			.then((response) => {
				return response === true;
			});
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}
}
